import React, { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { AccountingArchiveDocument, AccountingArchiveDocumentType, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Anchor, Group } from "@mantine/core";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { getDocumentById, useGetDocumentByCompanyIdAndType } from "@/services/Accounting";
import { keepPreviousData } from "@tanstack/react-query";
import { openBlobInNewTab } from "@/utils/FileUtils";
import { notifications } from "@mantine/notifications";
import SortableHeader from "@/components/molecules/SortableHeader";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import DataTable from "@/components/molecules/DataTable";
import Badge from "@/components/atoms/Badge";
import Select from "@/components/atoms/Select";
import AccountingDocumentArchiveEnum from "@/constants/enums/AccountingDocumentArchiveEnum";

import styles from "./styles.module.css";
import dayjs from "dayjs";

interface InvoiceArchiveTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<AccountingArchiveDocument>[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}

/**
 * A reusable invoice archive table component, with sorting, filtering, actions and pagination.
 * @param InvoiceArchiveTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const InvoiceArchiveTable: React.FC<InvoiceArchiveTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination = true,
  actionable: _actionable = true,
  initialRowsPerPage = 15,
}) => {
  const { currentCompany } = useAuth();
  const { t } = useTranslation();
  const [documentType, setDocumentType] = useState<AccountingArchiveDocumentType>("INVOICE");
  const [activePage, setActivePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const documentTypes = [
    { value: "INVOICE", label: t("common:invoice") },
    { value: "SETTLEMENT", label: t("common:settlement") },
  ];

  const { data: documents, isFetching: isFetchingDocuments } = useGetDocumentByCompanyIdAndType(
    currentCompany?.companyId as number,
    activePage - 1,
    rowsPerPage,
    "DESC",
    documentType,
    {
      enabled: !!currentCompany?.companyId,
      placeholderData: keepPreviousData,
    }
  );

  const handlePageChange = (page: number) => {
    setActivePage(page);
  };

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  const handleDocumentTitleClick = async (documentId: number, documentType: AccountingArchiveDocumentType) => {
    try {
      const response = await getDocumentById(documentId, documentType);
      const arrayBuffer = response.data;
      const contentType = response.headers["content-type"] || "application/pdf";

      openBlobInNewTab(arrayBuffer, contentType);
    } catch {
      notifications.show({
        title: t("notifications:generic.error"),
        message: t("notifications:generic.cannotOpenDocument"),
      });
    }
  };

  const baseColumns: ColumnDef<AccountingArchiveDocument>[] = useMemo(
    () => [
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:description")} />,
        id: "documentTitle",
        accessorKey: "documentTitle",
        cell: ({ row }) => (
          <Anchor onClick={() => handleDocumentTitleClick(row.original.originalId, row.original.documentType)}>
            {row.original.documentType === AccountingDocumentArchiveEnum.INVOICE
              ? t("common:invoice")
              : t("common:settlement")}
          </Anchor>
        ),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:type")} />,
        id: "documentType",
        accessorKey: "documentType",
        cell: ({ row }) => {
          return t("enumerables:documentTypes." + row.original.documentType);
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:date")} />,
        id: "created",
        accessorKey: "created",
        cell: ({ row }) => {
          return dayjs(row.original.created).format("DD.MM.YYYY");
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("commonOrders:buyer")} />,
        id: "buyerCompany",
        accessorKey: "buyerCompany",
        cell: ({ row }) => {
          return row.original.buyerCompany;
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("commonOrders:seller")} />,
        id: "sellerCompany",
        accessorKey: "sellerCompany",
        cell: ({ row }) => {
          return row.original.sellerCompany;
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={"status"} />,
        id: "paid",
        cell: ({ row }) => {
          return row.original.paid ? (
            <Badge color={"completed"} radius={"xs"}>
              {t("common:paid")}
            </Badge>
          ) : (
            <Badge color={"warning"} radius={"xs"}>
              {t("common:unpaid")}
            </Badge>
          );
        },
        maxSize: 100,
      },
    ],
    [t]
  );

  const columns = useVisibleColumns<AccountingArchiveDocument>(baseColumns, visibleColumns);

  return (
    <>
      <Group dir={"col"} className={styles.tableContainer}>
        <Select
          style={{ width: 200 }}
          data={documentTypes}
          value={documentType}
          onChange={(value) => setDocumentType(value as AccountingArchiveDocumentType)}
        />
        <DataTable
          data={documents?.documents ?? []}
          columns={columns}
          isLoading={isFetchingDocuments}
          mobileHeaderCell={0}
          showPagination={pagination}
          useServerSidePagination={true}
          initialSort={initialSort ?? undefined}
          initialRowsPerPage={initialRowsPerPage}
          activePage={activePage}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalItems={documents?.totalCount}
        />
      </Group>
    </>
  );
};

export default InvoiceArchiveTable;
